// Turns the text of a Victoria Police LEAP "Criminal Record" (priors) into a
// short list of hearings for question 24 of the legal aid application.
//
// The record is a fixed-column printout: COURT | DATE | DEGREE & CHARGE | COUNT | RESULT,
// newest hearing first. Input is one page at a time as lines of positioned words
// ({ words: [{ t, x0 }] }), from OCR on a scan or the text layer of a typed PDF.
// Nothing here touches the network; it runs in the lawyer's browser.

const DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;

/** @typedef {{ t: string, x0: number }} Word */
/** @typedef {{ words: Word[] }} Line */
/** @typedef {{ court: string, date: string, year: string, re: string, charges: { name: string, count: number }[], result: string }} Hearing */

/** Column start positions, read from the page's own header row. */
function findColumns(lines) {
  for (let i = 0; i < lines.length; i += 1) {
    // The header sometimes wraps "&" onto its own line, so look at this line and the next.
    const words = [...lines[i].words, ...(lines[i + 1]?.words || [])];
    const find = (re) => words.find((w) => re.test(w.t));
    const court = find(/^COURT$/i);
    const date = find(/^DATE$/i);
    const charge = find(/^DEGREE$/i);
    const count = find(/^COUNT$/i);
    const result = find(/^RESULT$/i);
    if (court && date && charge && result && date.x0 > court.x0 && result.x0 > date.x0) {
      return {
        headerIndex: lines[i + 1] && lines[i + 1].words.some((w) => w === result || w === count || w === charge) ? i + 1 : i,
        date: date.x0,
        charge: charge.x0,
        count: count ? count.x0 : (charge.x0 + result.x0) / 2,
        result: result.x0,
      };
    }
  }
  return null;
}

/** Split one line into its columns by x position. */
function splitColumns(line, cols) {
  const slack = (cols.charge - cols.date) * 0.25;
  const out = { court: [], date: [], charge: [], count: [], result: [] };
  for (const w of line.words) {
    const x = w.x0;
    if (x < cols.date - slack) out.court.push(w.t);
    else if (x < cols.charge - slack) out.date.push(w.t);
    else if (x < cols.count + slack) out.charge.push(w.t);
    else if (x < cols.result - slack / 2) out.count.push(w.t);
    else out.result.push(w.t);
  }
  const join = (a) => a.join(" ").replace(/\s+/g, " ").trim();
  return { court: join(out.court), date: join(out.date), charge: join(out.charge), count: join(out.count), result: join(out.result) };
}

function toYear(yy) {
  if (yy.length === 4) return yy;
  const n = Number(yy);
  const thisYear = new Date().getFullYear() % 100;
  return String(n <= thisYear ? 2000 + n : 1900 + n);
}

/**
 * Feed pages in order. Returns the running list of hearings (newest first).
 * @param {Line[]} lines one page
 * @param {{ hearings: Hearing[], current: Hearing | null }} state
 */
export function parsePriorsPage(lines, state = { hearings: [], current: null }) {
  const cols = findColumns(lines);
  if (!cols) return state;

  let pendingCourt = [];
  let continued = false;

  for (const line of lines.slice(cols.headerIndex + 1)) {
    const text = line.words.map((w) => w.t).join(" ");
    if (/continued next page|sensitive:\s*personal/i.test(text)) break;
    if (/^[-\s]+$/.test(text)) continue;

    const c = splitColumns(line, cols);
    // "Continued ... See charges above" marks a hearing carried over from the last page.
    // OCR doesn't always split that line cleanly into columns, so look at the whole line.
    if (/^continued\b/i.test(c.court) || /see\s+charges\s+above/i.test(text)) {
      continued = true;
      if (!c.date) continue;
    }

    // A date in the date column starts a hearing, unless it's the carry-over of the last one.
    const dateToken = c.date.split(" ").find((t) => DATE_RE.test(t));
    if (dateToken) {
      const [, , , yy] = dateToken.match(DATE_RE);
      const court = [c.court, ...pendingCourt].filter(Boolean).join(" ");
      pendingCourt = [];
      if (continued && state.current && state.current.date === dateToken) {
        // Same hearing, carried over from the previous page.
      } else {
        state.current = { court, date: dateToken, year: toYear(yy), re: "", charges: [], result: "" };
        state.hearings.push(state.current);
      }
      continued = false;
    } else if (c.court && state.current) {
      // Second line of the court name, e.g. "MAGISTRATES".
      if (!state.current.court.includes(c.court)) state.current.court = `${state.current.court} ${c.court}`.trim();
    }

    const h = state.current;
    if (!h) continue;

    // OCR sometimes reads a speck before the first letter as a quote mark.
    let charge = c.charge.replace(/^[^A-Za-z0-9(]+/, "");
    const re = charge.match(/\bre\s+(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
    if (re) {
      h.re = re[1];
      charge = charge.replace(re[0], "").trim();
    }
    if (charge && !/see\s+charges\s+above/i.test(charge)) {
      const count = Number((c.count.match(/\d+/) || [])[0] || 1);
      // A charge that wrapped onto a second line has no count and starts in lower case or mid-phrase.
      h.charges.push({ name: charge, count });
    }
    const result = c.result.replace(/^[|+]\s*/, "").replace(/\s*[|+]\s*/g, " ").trim();
    if (result && !/^\.{3,}$/.test(result)) h.result = `${h.result} ${result}`.trim();
  }
  return state;
}

const sentenceCase = (s) => {
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());

export function formatCourt(court = "") {
  const name = titleCase(court.trim());
  return name
    .replace(/\bMagistrates\b(?! Court)/, "Magistrates' Court")
    .replace(/\bChildrens\b(?! Court)/, "Children's Court")
    .replace(/\b(County|Supreme)\b(?! Court)/, "$1 Court");
}

/** The outcome a lawyer would write in a priors summary, from the long RESULT text. */
export function summarisePenalty(result = "") {
  const r = result.toUpperCase().replace(/\s+/g, " ");
  const parts = [];
  const add = (s) => { if (s && !parts.includes(s)) parts.push(s); };
  const period = (m) => m.replace(/\s+/g, " ").toLowerCase();

  if (/\bPROVEN\b/.test(r)) add("breach proven");
  if (/ORIGINAL ORDER HAS BEEN CONFIRMED/.test(r)) add("original order confirmed");
  if (/WITHOUT CONVICTION/.test(r)) add("no conviction recorded");
  else if (/\bCONVICTED\b|WITH CONVICTION/.test(r)) add("convicted");

  let m;
  if ((m = r.match(/IMPRISON(?:ED|MENT)[^.]*?(\d+\s*(?:YEARS?|MONTHS?|WEEKS?|DAYS?))/))) add(`imprisonment ${period(m[1])}`);
  if ((m = r.match(/COMMUNITY CORRECTION ORDER\s*FOR\s*(\d+\s*(?:YEARS?|MONTHS?))/))) add(`Community Correction Order ${period(m[1])}`);
  else if (/COMMUNITY CORRECTION ORDER/.test(r)) add("Community Correction Order");
  if ((m = r.match(/(\d+)\s*HOURS? OF COMMUNITY WORK/))) add(`${m[1]} hours community work`);
  if ((m = r.match(/FINED?\s*\$?\s*([\d,]+(?:\.\d{2})?)/)) || (m = r.match(/\$\s*([\d,]+(?:\.\d{2})?)/))) add(`fined $${m[1]}`);
  if (/ADJOURNED\b[^.]*UNDERTAKING|ADJOURNED UNDERTAKING|\bADU\b/.test(r)) add("adjourned undertaking");
  else if (/\bADJOURNED\b/.test(r)) add("adjourned");
  if (/\bDISCHARGED\b/.test(r)) add("discharged");
  if (/DIVERSION/.test(r)) add("diversion");
  if (/\bDISMISSED\b/.test(r)) add("dismissed");
  if (/STRUCK OUT/.test(r)) add("struck out");
  if ((m = r.match(/LICENCE CANCELLED AND DISQUALIFIED FOR (\d+\s*(?:YEARS?|MONTHS?))/))) add(`licence cancelled, disqualified ${period(m[1])}`);
  if (/FORFEITED/.test(r)) add("items forfeited");

  if (parts.length === 0) {
    const first = result.split(/(?<=\.)\s/)[0].trim();
    return first.length > 80 ? `${first.slice(0, 77)}...` : first;
  }
  const text = parts.join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Merge charge lines that are really one charge wrapped onto two lines. */
function tidyCharges(charges) {
  const out = [];
  for (const c of charges) {
    const prev = out[out.length - 1];
    if (prev && /^BREACH$/i.test(c.name)) prev.name = `${prev.name} (breach)`;
    else if (prev && c.count === 1 && /^(OF|BY|IN|ON|TO|AND|WITH|WHILST|-)\b/i.test(c.name)) prev.name = `${prev.name} ${c.name}`;
    else out.push({ ...c });
  }
  return out;
}

/** @param {Hearing} h */
export function formatHearing(h) {
  const charges = tidyCharges(h.charges)
    .map((c) => `${sentenceCase(c.name)}${c.count > 1 ? ` x${c.count}` : ""}`)
    .join("; ");
  const re = h.re ? ` (re ${h.re})` : "";
  const penalty = summarisePenalty(h.result);
  return [`${h.date} ${formatCourt(h.court)}${re}`, charges, penalty].filter(Boolean).join(" – ");
}

/** The fields question 24 fills in: the full list, plus the most recent prior on its own. */
export function priorsToFields(hearings) {
  // A hearing with no charges is the tail of one carried over a page break whose
  // start wasn't read (or whose date OCR misread), so it isn't a prior of its own.
  const usable = hearings.filter((h) => h.charges.length);
  const latest = usable[0];
  return {
    details: usable.map(formatHearing).join("\n"),
    year: latest ? latest.year : "",
    offence: latest ? tidyCharges(latest.charges).map((c) => sentenceCase(c.name)).join("; ") : "",
    penalty: latest ? summarisePenalty(latest.result) : "",
    count: usable.length,
  };
}
